/** Training-side thresholds. Same rule as config/audio.ts: no magic numbers elsewhere. */

export const DEFAULTS = {
  bpm: 55,
  beatsPerChord: 4 as const,
  durationMin: 5,
  /** What a brand-new user gets offered before any history exists. */
  starterChords: ['Am', 'C'],
  starterProgressionId: 'first-pair',
  countInBeats: 4,
} as const;

export const DURATION_OPTIONS = [3, 5, 10] as const;
export const BEATS_OPTIONS = [2, 4, 8] as const;

export const TRANSITION = {
  /**
   * A chord counts as "held" once the smoothed score stays above the correct
   * threshold for this many consecutive frames (~12 fps). Stops a single lucky
   * spectral frame from completing a transition.
   */
  stableFrames: 3,
  /** Transitions faster than this are physically implausible — discard. */
  minPlausibleSec: 0.08,
  /** Transitions slower than this are not a transition, they are a pause. */
  maxPlausibleSec: 6,
} as const;

export const RHYTHM = {
  /** |error| under this counts as on time. Around the musical JND (40–60 ms). */
  onTimeMs: 55,
  /** |error| under this counts as almost. */
  closeMs: 110,
  /** Fraction of the beat interval inside which an onset is attributed to a beat. */
  hitWindowFraction: 0.35,
  /** …capped, so slow tempos do not create a two-second window. */
  hitWindowMaxMs: 260,
  /** Hits collected before the systematic input-latency offset is estimated. */
  latencyEstimateHits: 8,
  /** The estimated offset is clamped to this, in ms, in both directions. */
  latencyClampMs: 250,
  /** Score mapping: |corrected error| ≥ this scores 0 for that beat. */
  zeroScoreMs: 200,
} as const;

export const ADAPTIVE_TEMPO = {
  /** Attempts required before the tempo may move at all. */
  minSamples: 8,
  /** Accuracy over the recent window that earns a tempo increase. */
  raiseAccuracy: 0.85,
  /** Accuracy under which the tempo drops. */
  lowerAccuracy: 0.55,
  /** Consecutive successful slots also required before raising. */
  raiseStreak: 6,
  /** BPM step. */
  step: 5,
  /** Seconds that must pass between two tempo changes. */
  cooldownSec: 25,
  /** Window of recent attempts the decision looks at. */
  window: 12,
  /** Hard limits for automatic movement, independent of the slider range. */
  minBpm: 40,
  maxBpm: 140,
} as const;

export const COMFORT = {
  /** Success rate that defines "comfortable" at a given tempo. */
  successRate: 0.8,
  /** Attempts needed in a BPM bucket before it can be trusted. */
  minAttemptsPerBucket: 6,
  /** BPM values are bucketed to this grid before aggregation. */
  bucketSize: 5,
} as const;

export const RECOMMENDATION = {
  /** A transition below this success rate is a candidate for "weak". */
  weakSuccessRate: 0.7,
  /** Attempts needed before we are willing to call something weak. */
  minAttempts: 6,
  /** Clarity below this makes a chord a candidate for Clean Chords. */
  lowClarity: 70,
  /** Rhythm score below this makes rhythm the priority. */
  weakRhythm: 70,
  /** Days after which a strong skill is offered for spaced review. */
  reviewAfterDays: 5,
  /** Recommended session length for focused remedial work, in minutes. */
  focusDurationMin: 3,
  /** Recommended tempo is the comfortable tempo minus this margin. */
  bpmSafetyMargin: 5,
  /**
   * Recent attempts kept per transition. Weakness is judged on this window
   * when it is full enough, so improvement actually retires a recommendation.
   */
  recentWindow: 12,
} as const;

export const SESSION = {
  /** Analysis of a chord slot starts this fraction into the slot. */
  evaluationStartFraction: 0.15,
  /** …and ends here, leaving room for the player to move to the next chord. */
  evaluationEndFraction: 0.95,
  /** Shortest evaluation window we will accept, in seconds. */
  minEvaluationSec: 0.25,
} as const;
